import {
  Box,
  getTextColorByPath,
  HStack,
  Icon,
  Text,
  ThemeColorPath,
  ThemeColorTextPath,
  ThemeIconKey,
  VStack,
} from 'platform/foundation';
import styled from 'styled-components';
import {match, Pattern} from 'ts-pattern';

import {ReactNode} from 'react';

import {always} from 'ramda';

type InfoboxVariant = 'error' | 'warning' | 'info' | 'success';

type InfoboxSeverity = 'neutral' | 'highlight';

interface InfoboxText {
  value: string | ReactNode;
}

interface InfoboxList {
  list: InfoboxText[];
}

interface InfoboxProps {
  variant: InfoboxVariant;
  severity?: InfoboxSeverity;
  data: (InfoboxText | InfoboxList)[];
}

// eslint-disable-next-line eag/no-css-property
const StyledList = styled.ul<{textColor?: ThemeColorTextPath}>`
  list-style-position: inside;
  ${({theme, textColor}) => textColor && `color: ${getTextColorByPath(textColor, theme)};`}
  font-size: ${({theme}) => theme.fontSizes.text.xSmall};
  line-height: ${({theme}) => theme.lineHeights.text.xSmall};
`;

export function Infobox(props: InfoboxProps) {
  const textSeverity = props.severity ?? 'neutral';

  const settings = match<
    InfoboxVariant,
    {
      bgColor: ThemeColorPath;
      icon: ThemeIconKey;
      iconColor: ThemeColorPath;
      borderColor: ThemeColorPath;
    }
  >(props.variant)
    .with(
      'error',
      always({
        bgColor: 'palettes.red.10.100',
        icon: 'alert/error',
        iconColor: 'severity.danger',
        borderColor: 'palettes.red.70.100',
      })
    )
    .with(
      'warning',
      always({
        bgColor: 'palettes.orange.10.100',
        icon: 'alert/warning',
        iconColor: 'severity.warning',
        borderColor: 'palettes.orange.70.100',
      })
    )
    .with(
      'info',
      always({
        bgColor: 'palettes.blue.10.100',
        icon: 'action/info',
        iconColor: 'severity.informational',
        borderColor: 'palettes.blue.70.100',
      })
    )
    .with(
      'success',
      always({
        bgColor: 'palettes.green.10.100',
        icon: 'action/info',
        iconColor: 'severity.success',
        borderColor: 'palettes.green.70.100',
      })
    )
    .exhaustive();

  const textColor = match<[InfoboxSeverity, InfoboxVariant], ThemeColorTextPath>([
    textSeverity,
    props.variant,
  ])
    .with(['neutral', Pattern.any], always('primary'))
    .with(['highlight', 'error'], always('danger'))
    .with(['highlight', 'warning'], always('warning'))
    .with(['highlight', 'info'], always('link'))
    .with(['highlight', 'success'], always('success'))
    .exhaustive();

  return (
    <Box
      borderRadius="medium"
      borderColor={settings.borderColor}
      border="1px solid"
      backgroundColor={settings.bgColor}
      minHeight={13}
      padding={4}
    >
      <HStack spacing={10} width="100%">
        <Icon value={settings.icon} color={settings.iconColor} />
        <VStack>
          {props.data.map((item, index) =>
            'value' in item ? (
              // eslint-disable-next-line react/no-array-index-key
              <Text size="xSmall" color={textColor} key={`infobox-${index}`}>
                {item.value}
              </Text>
            ) : (
              // eslint-disable-next-line react/no-array-index-key
              <StyledList textColor={textColor} key={`infobox-${index}-list-${index}`}>
                {item.list.map((listItem, listIndex) => (
                  // eslint-disable-next-line react/no-array-index-key
                  <li key={`infobox-${index}-list-${index}-${listIndex}`}>{listItem.value}</li>
                ))}
              </StyledList>
            )
          )}
        </VStack>
      </HStack>
    </Box>
  );
}
